import config from 'isomorphic-config'

import analytics from '@/shared-utils/analytics'

import type { TypeaheadBadgeKind } from './utils/getTypeaheadBadge'

const productMapper = (productType: keyof typeof config.client.typeahead.location.endpoint) => {
  const mappings = {
    hotels: 'hotel',
    hotelsV2: 'hotel',
    cars: 'car',
    packagesOrigin: 'bundle',
    packagesDestination: 'bundle',
    flights: 'air',
  } as const

  return mappings[productType]
}

export default function fireTypeaheadClickEvent(
  typeAheadEndpoint: keyof typeof config.client.typeahead.location.endpoint,
  itemName?: string,
) {
  analytics.fireGA4Event({
    event: 'select_content',
    attributes: {
      type: 'search_form',
      item_name: itemName,
      product_name: productMapper(typeAheadEndpoint),
    },
  })
}

/**
 * Maps a typeahead item type to the analytics "entity-type" concept. Spaces
 * within a concept use `-`; `_` is reserved for separating concepts.
 */
const TYPEAHEAD_ENTITY_BY_TYPE: Record<string, string> = {
  CITY: 'city',
  HOTEL: 'hotel',
  AIRPORT: 'airport',
  NEIGHBOURHOOD: 'neighbourhood',
  POI: 'poi',
  GEO: 'geo',
  AI_QUERY: 'ai-query',
}

function getTypeaheadEntity(type: string): string {
  return TYPEAHEAD_ENTITY_BY_TYPE[type] ?? type.toLowerCase().replaceAll('_', '-')
}

export const DEFAULT_TYPEAHEAD_CHIP = 'default'

export type TypeaheadChip = TypeaheadBadgeKind | typeof DEFAULT_TYPEAHEAD_CHIP

/** Canonicalizes search-type synonyms so both typeahead code paths emit one token. */
const SEARCH_TYPE_ALIASES: Record<string, string> = {
  recommendations: 'recommended',
}

export type BuildTypeaheadItemNameParams = {
  chip: TypeaheadChip
  type: string
  searchType?: string
}

/**
 * Builds a structured `item_name` for a typeahead result, following the analytics
 * convention `[chip-name]_[entity-type]_[search-results]` where `_` separates
 * concepts and `-` replaces spaces within a concept. `chip` is `default` for a
 * non-chipped result, or the badge kind (`popular` / `trending`) when one applies.
 * e.g. `default_city_recent-searches`, `popular_city_typein`.
 */
export function buildTypeaheadItemName({ chip, type, searchType }: BuildTypeaheadItemNameParams): string {
  const parts = [chip, getTypeaheadEntity(type)]
  if (searchType) {
    const canonical = SEARCH_TYPE_ALIASES[searchType] ?? searchType
    parts.push(canonical.replaceAll('_', '-'))
  }
  return parts.join('_')
}

export function fireAutocompleteOpenEvent(
  typeAheadEndpoint: keyof typeof config.client.typeahead.location.endpoint,
  itemName?: string,
) {
  analytics.fireGA4Event({
    event: 'display',
    attributes: {
      type: 'search_form',
      item_name: itemName ?? null,
      product_name: productMapper(typeAheadEndpoint),
      page_name: 'homepage',
    },
  })
}
